import { content } from "../../config/content.js";
import { appRoute, publicAsset, sectionId } from "../../config/paths.js";
import { getCenteredScrollTop } from "../components/Nav.js";

const logoMark = `<img class="footer__logo" src="${publicAsset("logo_black.png")}" alt="${content.brand}" width="140" height="32" decoding="async" />`;

const socialIcons = {
  instagram: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M7.75 2h8.5A5.75 5.75 0 0122 7.75v8.5A5.75 5.75 0 0116.25 22h-8.5A5.75 5.75 0 012 16.25v-8.5A5.75 5.75 0 017.75 2zm0 1.5A4.25 4.25 0 003.5 7.75v8.5A4.25 4.25 0 007.75 20.5h8.5a4.25 4.25 0 004.25-4.25v-8.5A4.25 4.25 0 0016.25 3.5h-8.5zM12 7a5 5 0 110 10 5 5 0 010-10zm0 1.5a3.5 3.5 0 100 7 3.5 3.5 0 000-7zM17.5 6a1 1 0 110 2 1 1 0 010-2z"/></svg>`,
  linkedin: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 114.126 0 2.063 2.063 0 01-2.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>`,
  facebook: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.84c0-2.5 1.49-3.89 3.77-3.89 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.88h2.78l-.44 2.91h-2.34V22c4.78-.76 8.44-4.92 8.44-9.94z"/></svg>`,
  x: `<svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>`,
};

function renderColumn(column) {
  const links = column.links
    .map((link) => `<li><a class="footer__link" href="${link.href}">${link.label}</a></li>`)
    .join("");

  return `
    <div class="footer__column">
      <h3 class="footer__column-title">${column.title}</h3>
      <ul class="footer__links">${links}</ul>
    </div>
  `;
}

function renderSocial(social) {
  return social
    .map(
      (item) => `
        <a class="footer__social-link" href="${item.href}" target="_blank" rel="noopener noreferrer" aria-label="${item.label}">
          ${socialIcons[item.icon]}
        </a>
      `
    )
    .join("");
}

export function createFooter() {
  const { footer } = content;
  const footerEl = document.createElement("footer");
  footerEl.className = "footer";

  footerEl.innerHTML = `
    <div class="footer__glow" aria-hidden="true"></div>

    <div class="footer__main">
      <div class="footer__brand-col">
        <a class="footer__brand" href="${appRoute("hero")}" aria-label="${content.brand}">
          ${logoMark}
        </a>
        <p class="footer__brand-tagline">AI-vetted teams for startups and growing tech companies.</p>
        <div class="footer__social">${renderSocial(footer.social)}</div>
      </div>

      <div class="footer__columns">
        ${footer.columns.map(renderColumn).join("")}
      </div>
    </div>

    <div class="footer__bottom">
      <p class="footer__copyright">&copy; ${footer.year} ${content.brand}. All rights reserved.</p>
      <ul class="footer__legal">
        ${footer.legal.map((item) => `<li><a class="footer__link" href="${item.href}">${item.label}</a></li>`).join("")}
      </ul>
    </div>

    <p class="footer__watermark" aria-hidden="true">${content.brand}</p>
  `;

  footerEl.querySelectorAll(".footer__link").forEach((link) => {
    link.addEventListener("click", (event) => {
      const href = link.getAttribute("href");
      if (!href) return;

      const targetId = sectionId(href);
      if (!targetId) return;

      try {
        const next = new URL(href, window.location.origin);
        const here = window.location.pathname.replace(/\/+$/, "") || "/";
        const there = next.pathname.replace(/\/+$/, "") || "/";
        if (there !== here) return;
      } catch {
        return;
      }

      const target = document.getElementById(targetId);
      if (!target) return;

      event.preventDefault();
      window.scrollTo({ top: getCenteredScrollTop(target), behavior: "smooth" });
      history.pushState(null, "", href);
    });
  });

  return footerEl;
}
